import { useMemo } from 'react'
import { CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, InfoTooltip } from './shared'

interface StageData {
  stage: string
  avgDays: number
  count: number
}

interface CycleTimeReportData {
  summary: {
    avgTotalCycle: number
    avgEstimateTime: number
    avgProposalTime: number
  }
  chartData: {
    stages: StageData[]
  }
}

interface CycleTimeReportProps {
  data: CycleTimeReportData
}

function TimelineBar({ stages }: { stages: StageData[] }) {
  const totalDays = stages.reduce((sum, st) => sum + st.avgDays, 0) || 1

  return (
    <div className="rep-timeline">
      <div className="rep-timeline-track">
        {stages.map((stage, idx) => {
          const pct = (stage.avgDays / totalDays) * 100
          const minWidth = Math.max(pct, 8)
          const stageLabel = stage.stage
            .replace('Estimate Creation → Approval', 'Estimate')
            .replace('Proposal Sent → Signed', 'Proposal')
            .replace('Total Cycle', 'Total')

          return (
            <div
              key={idx}
              className="rep-timeline-segment"
              style={{
                width: `${minWidth}%`,
                backgroundColor: CHART_PALETTE[idx % CHART_PALETTE.length],
              }}
              title={`${stageLabel}: ${stage.avgDays} days`}
            >
              <span className="rep-timeline-seg-label">{stageLabel}</span>
              <span className="rep-timeline-seg-days">{stage.avgDays}d</span>
            </div>
          )
        })}
      </div>

      <div className="rep-timeline-markers">
        {stages.slice(0, -1).map((_, cumIdx) => {
          let cumSum = 0
          for (let j = 0; j <= cumIdx; j++) {
            cumSum += stages[j].avgDays
          }
          const pct = (cumSum / totalDays) * 100
          return (
            <div
              key={cumIdx}
              className="rep-timeline-marker"
              style={{ left: `${pct}%` }}
            >
              {cumSum}d
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function CycleTimeReport({ data }: CycleTimeReportProps) {
  const { summary, chartData } = data

  const stagesDisplay = useMemo(() => chartData?.stages || [], [chartData])

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Avg Total Cycle" value={`${summary?.avgTotalCycle ?? 0} days`} color="#0074F4" />
        <StatCard title="Estimate Phase" value={`${summary?.avgEstimateTime ?? 0} days`} color="#8B47FB" />
        <StatCard title="Proposal Phase" value={`${summary?.avgProposalTime ?? 0} days`} color="#D447FB" />
      </div>

      <div className="rep-chart-card">
        <InfoTooltip text="A swim-lane timeline shows workflow phases as connected segments, making the total journey and bottlenecks visually obvious — it tells a story that isolated bars cannot." />
        {stagesDisplay.length > 0 ? (
          <TimelineBar stages={stagesDisplay} />
        ) : (
          <div className="rep-loading">No data available</div>
        )}
      </div>

      {stagesDisplay.length > 0 && (
        <div className="rep-chart-card">
          <h4 className="rep-table-title">Stage Details</h4>
          <table className="rep-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th className="ta-right">Avg Days</th>
                <th className="ta-right">Sample Size</th>
              </tr>
            </thead>
            <tbody>
              {stagesDisplay.map((stage, idx) => (
                <tr key={idx}>
                  <td>{stage.stage}</td>
                  <td className="ta-right">{stage.avgDays}</td>
                  <td className="ta-right">{stage.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
